import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Type definitions for detail responses
export interface ReceivedIncomeDetail {
  booking_id: string;
  guest_name: string;
  check_in: string;
  check_out: string;
  property_short_name: string;
  property_id: string;
  origin_name: string | null;
  income_records: Array<{
    id: string;
    payout: number;
    payout_date: string;
    payment_type: string;
    income_type: string;
    fiscal_invoice: boolean;
  }>;
  total_payout: number;
}

export interface FutureIncomeDetail {
  booking_id: string;
  guest_name: string;
  check_in: string;
  check_out: string;
  property_short_name: string;
  property_id: string;
  origin_name: string | null;
  estimated_payout: number;
  nights: number;
  status: string;
  stay_completed: boolean;
  total_income_received: number;
  pending_amount: number;
  has_partial_payment: boolean;
}

export interface ExpenseDetail {
  id: string;
  expense_date: string;
  description: string | null;
  amount: number;
  status: string;
  payment_method: string;
  property_id: string;
  property_short_name: string | null;
  category_name: string | null;
  subcategory_name: string | null;
}

export interface IncomeByOrigin {
  origin_id: string;
  origin_name: string;
  total_income: number;
  booking_count: number;
}

interface UseDetailsParams {
  year: number;
  month: number;
  propertyIds?: string[];
  organizationIds?: string[];
  enabled?: boolean;
}

/**
 * Hook to fetch detailed breakdown of received income
 * Only loads when enabled (lazy loading for expanded sections)
 */
export const useReceivedIncomeDetails = ({
  year,
  month,
  propertyIds,
  organizationIds,
  enabled = false,
}: UseDetailsParams) => {
  return useQuery({
    queryKey: ["received-income-details", year, month, propertyIds, organizationIds],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_received_income_details", {
        p_year: year,
        p_month: month,
        p_property_ids: propertyIds?.length ? propertyIds : null,
        p_organization_ids: organizationIds?.length ? organizationIds : null,
      });

      if (error) {
        console.error("Error fetching received income details:", error);
        throw error;
      }

      return (data || []) as unknown as ReceivedIncomeDetail[];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch detailed breakdown of future income
 * Only loads when enabled (lazy loading for expanded sections)
 */
export const useFutureIncomeDetails = ({
  year,
  month,
  propertyIds,
  organizationIds,
  enabled = false,
}: UseDetailsParams) => {
  return useQuery({
    queryKey: ["future-income-details", year, month, propertyIds, organizationIds],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_future_income_details", {
        p_year: year,
        p_month: month,
        p_property_ids: propertyIds?.length ? propertyIds : null,
        p_organization_ids: organizationIds?.length ? organizationIds : null,
      });

      if (error) {
        console.error("Error fetching future income details:", error);
        throw error;
      }

      return (data || []) as unknown as FutureIncomeDetail[];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch detailed breakdown of expenses
 * Only loads when enabled (lazy loading for expanded sections)
 */
export const useExpensesDetails = ({
  year,
  month,
  propertyIds,
  organizationIds,
  enabled = false,
}: UseDetailsParams) => {
  return useQuery({
    queryKey: ["expenses-details", year, month, propertyIds, organizationIds],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_expenses_details", {
        p_year: year,
        p_month: month,
        p_property_ids: propertyIds?.length ? propertyIds : null,
        p_organization_ids: organizationIds?.length ? organizationIds : null,
      });

      if (error) {
        console.error("Error fetching expenses details:", error);
        throw error;
      }

      return (data || []) as unknown as ExpenseDetail[];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch income grouped by origin (top 5)
 * Respects same filters as other dashboard functions
 */
export const useIncomeByOrigin = ({
  year,
  month,
  propertyIds,
  organizationIds,
  enabled = true,
}: UseDetailsParams) => {
  return useQuery({
    queryKey: ["income-by-origin", year, month, propertyIds, organizationIds],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_income_by_origin", {
        p_year: year,
        p_month: month,
        p_property_ids: propertyIds?.length ? propertyIds : null,
        p_organization_ids: organizationIds?.length ? organizationIds : null,
      });

      if (error) {
        console.error("Error fetching income by origin:", error);
        throw error;
      }

      return (data || []) as unknown as IncomeByOrigin[];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
